import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import ChatTabs from '../components/ChatTabs';
import { Response } from '../components/ai/response';

function ChatPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [botTypingMessage, setBotTypingMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalSingle, setIsModalSingleOpen] = useState(false);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState(null);
  const [botAudioUrl, setBotAudioUrl] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [plan, setPlan] = useState([]);
  const bottomRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [isHovered, setIsHovered] = useState(false);
  const [playingAudioIndex, setPlayingAudioIndex] = useState(null);
  const audioRefs = useRef({});

  const utteranceRef = useRef(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [lastMessage, setLastMessage] = useState('');
  const [model, setModel] = useState('KHUSUS');

  const [isVoiceConnected, setIsVoiceConnected] = useState(false);
  const [pcRef, setPcRef] = useState(null);

  useEffect(() => {
    return () => {
      Object.values(audioRefs.current).forEach(audio => {
        if (!audio.paused) {
          audio.pause();
          audio.currentTime = 0;
        }
      });

      if (speechSynthesis.speaking || speechSynthesis.pending) {
        speechSynthesis.cancel();
      }

      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const speak = (text) => {
    if (isSpeaking && text === lastMessage) {
      speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }

    speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'id-ID';

    utterance.onstart = () => {
      setIsSpeaking(true);
      setLastMessage(text);
      utteranceRef.current = utterance;
    };

    utterance.onend = () => {
      setIsSpeaking(false);
      utteranceRef.current = null;
    };

    speechSynthesis.speak(utterance);
  };

  const fetchPlan = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/user/current-plan`, {
        withCredentials: true,
      });
      setPlan(res.data.data || []);
    } catch (err) {
      Swal.fire('Oops!', (err.response?.data?.message || "Silakan coba lagi."), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlan();
  }, []);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  useEffect(() => {
    axios.get(process.env.REACT_APP_API_BASE_URL + '/chat/sessions', {
      withCredentials: true
    }).then(res => {
      setSessions(res.data);
      if (!sessionId && res.data.length > 0) {
        // Option-ally: don't auto-redirect, let it be blank, or redirect to first
        // navigate(`/dashboard/chat/${res.data[0].id}`, { replace: true });
      }
    });
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      setActiveSession(null);
      setMessages([]);
      return;
    }

    const found = sessions.find(s => String(s.id) === String(sessionId));
    if (found) {
      setActiveSession(found);
    } else if (sessions.length > 0) {
      // If not in sessions but we have sessions, maybe it's invalid or just not in list
      setActiveSession({ id: sessionId });
    } else {
      setActiveSession({ id: sessionId });
    }

    axios.get(`${process.env.REACT_APP_API_BASE_URL}/chat/session/${sessionId}`, {
      withCredentials: true
    }).then(res => {
      setMessages(res.data.messages || []);
    });
  }, [sessionId, sessions.length]);

  const simulateTyping = (text, file = null, audioUrl = null, tp = 'text', skip = false) => {

    if (skip) {
      setMessages(prev => [...prev, { role: 'bot', message: text, exportUrl: file, audioUrl: audioUrl, type: tp }]);
      setBotTypingMessage('');

      stopAudio();

      const audioKey = `bot-${messages.length + 1}`;
      const newAudio = new Audio(`${process.env.REACT_APP_API_BASE_URL}/${audioUrl}`);

      Object.keys(audioRefs.current).forEach((key) => {
        audioRefs.current[key].pause();
        audioRefs.current[key].currentTime = 0;
      });

      audioRefs.current[audioKey] = newAudio;
      newAudio.play();
      setPlayingAudioIndex(messages.length + 1);

      newAudio.onended = () => {
        setPlayingAudioIndex(null);
      };

      return;
    }

    setBotTypingMessage('');
    let index = 0;
    const typingSpeed = 5;

    const type = () => {
      if (index < text.length) {
        setBotTypingMessage(prev => prev + text.charAt(index));
        index++;
        setTimeout(type, typingSpeed);
      } else {
        setMessages(prev => [...prev, { role: 'bot', message: text, exportUrl: file, audioUrl: audioUrl, type: tp }]);
        setBotTypingMessage('');
      }
    };

    type();
  };

  const handleSend = async () => {
    if (!input.trim() || !activeSession) return;
    speechSynthesis.cancel();
    Object.keys(audioRefs.current).forEach((key) => {
      audioRefs.current[key].pause();
      audioRefs.current[key].currentTime = 0;
    });
    setPlayingAudioIndex(null);

    const newMessage = { role: 'user', message: input, type: 'text' };
    setMessages(prev => [...prev, newMessage]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(process.env.REACT_APP_API_BASE_URL + '/chat/generate', {
        sessionId: activeSession.id,
        question: input,
        model: model
      }, { withCredentials: true });

      const answer = res.data.answer || 'Tidak ada jawaban.';
      const file = res.data.exportUrl || '';
      const audio = res.data.audioUrl || '';
      simulateTyping(answer, file, audio, 'text');
    } catch (err) {
      simulateTyping('Gagal mendapatkan jawaban.');
    } finally {
      setLoading(false);
    }
  };

  const handleNewSession = (session) => {
    setSessions(prev => [session, ...prev]);
    navigate(`/dashboard/chat/${session.id}`);
  };

  const startVoiceChatSingle = () => {
    setIsModalSingleOpen(true);
    Object.keys(audioRefs.current).forEach((key) => {
      audioRefs.current[key].pause();
      audioRefs.current[key].currentTime = 0;
    });
    setPlayingAudioIndex(null);
    speechSynthesis.cancel();
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then(stream => {
        mediaRecorderRef.current = new MediaRecorder(stream, {
          mimeType: 'audio/webm;codecs=opus',
          audioBitsPerSecond: 32000
        });
        mediaRecorderRef.current.start();
        setIsRecording(true);
        audioChunksRef.current = [];

        mediaRecorderRef.current.ondataavailable = event => {
          audioChunksRef.current.push(event.data);
        };

        mediaRecorderRef.current.onstop = async () => {
          const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          const formData = new FormData();
          formData.append('audio', blob, 'voice_input.webm');
          formData.append('sessionId', activeSession.id);
          formData.append('model', model);

          try {

            Swal.fire({
              title: 'Mohon Tunggu',
              text: 'Sedang memproses rekaman...',
              allowOutsideClick: false,
              didOpen: () => {
                Swal.showLoading();
              }
            });

            const res = await axios.post(process.env.REACT_APP_API_BASE_URL + '/chat/voice', formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
              withCredentials: true
            });

            Swal.close();
            closeModal();

            const { answer, audioPath, question, questBlob } = res.data;

            console.log(res.data);

            setMessages(prev => [...prev, {
              role: 'user',
              type: 'audio',
              audioUrl: questBlob ?? null,
              message: question ?? null
            }]);

            simulateTyping(answer, null, audioPath, 'audio', true);

          } catch (err) {
            console.log(err);
            simulateTyping('Gagal mendapatkan jawaban.');
          }
        };
      })
      .catch(err => {
        Swal.fire("Error", "Akses mikrofon ditolak.", "error");
        setIsModalSingleOpen(false);
      });
  };
  const startVoiceChat = async () => {
    setIsModalOpen(true);

    try {
      const resp = await axios.post(
        process.env.REACT_APP_API_BASE_URL + "/realtime/session"
      );
      const { client_secret } = resp.data;

      const pc = new RTCPeerConnection();
      setPcRef(pc);

      // --- mic input
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      // --- speaker output
      const audioEl = document.createElement("audio");
      audioEl.autoplay = true;
      pc.ontrack = event => {
        audioEl.srcObject = event.streams[0];
      };

      // --- data channel (untuk log teks)
      const dc = pc.createDataChannel("oai-events");
      dc.onmessage = (evt) => {
        const data = JSON.parse(evt.data);
        console.log("OAI Event:", data);

        if (data.type === "response.output_text.delta") {
          axios.post(process.env.REACT_APP_API_BASE_URL + "/chat/log", {
            sessionId: activeSession.id,
            role: "bot",
            message: data.delta
          }, { withCredentials: true });
        }
      };

      // --- offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const sdpResp = await fetch("https://api.openai.com/v1/realtime?model=gpt-realtime&voice=verse", {
        method: "POST",
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${client_secret.value}`,
          "Content-Type": "application/sdp"
        }
      });

      const answerSDP = await sdpResp.text();
      await pc.setRemoteDescription({ type: "answer", sdp: answerSDP });

      setIsVoiceConnected(true);
      setIsRecording(true);

    } catch (err) {
      console.error("VoiceChat error:", err);
      Swal.fire("Error", "Gagal memulai voice chat.", "error");
      setIsModalOpen(false);
    }
  };

  const stopVoiceChat = () => {
    if (pcRef) {
      pcRef.close();
      setPcRef(null);
    }
    setIsVoiceConnected(false);
    setIsRecording(false);
    setIsModalOpen(false);
  };
  // const startVoiceChat = () => {
  //   setIsModalOpen(true);
  //   Object.keys(audioRefs.current).forEach((key) => {
  //     audioRefs.current[key].pause();
  //     audioRefs.current[key].currentTime = 0;
  //   });
  //   setPlayingAudioIndex(null);
  //   speechSynthesis.cancel();
  //   navigator.mediaDevices.getUserMedia({ audio: true })
  //     .then(stream => {
  //       mediaRecorderRef.current = new MediaRecorder(stream, {
  //         mimeType: 'audio/webm;codecs=opus',
  //         audioBitsPerSecond: 32000
  //       });
  //       mediaRecorderRef.current.start();
  //       setIsRecording(true);
  //       audioChunksRef.current = [];

  //       mediaRecorderRef.current.ondataavailable = event => {
  //         audioChunksRef.current.push(event.data);
  //       };

  //       mediaRecorderRef.current.onstop = async () => {
  //         const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
  //         const formData = new FormData();
  //         formData.append('audio', blob, 'voice_input.webm');
  //         formData.append('sessionId', activeSession.id);

  //         try {

  //           Swal.fire({
  //             title: 'Mohon Tunggu',
  //             text: 'Sedang memproses rekaman...',
  //             allowOutsideClick: false,
  //             didOpen: () => {
  //               Swal.showLoading();
  //             }
  //           });

  //           const res = await axios.post(process.env.REACT_APP_API_BASE_URL+'/chat/voice', formData, {
  //             headers: {
  //               'Content-Type': 'multipart/form-data',
  //             },
  //             withCredentials: true
  //           });

  //           Swal.close();
  //           closeModal();

  //           const { answer, audioPath, question, questBlob } = res.data;

  //           console.log(res.data);

  //           setMessages(prev => [...prev, {
  //             role: 'user',
  //             type: 'audio',
  //             audioUrl: questBlob ?? null,
  //             message: question ?? null
  //           }]);

  //           simulateTyping(answer, null, audioPath, 'audio', true);

  //         } catch (err) {
  //           console.log(err);
  //           simulateTyping('Gagal mendapatkan jawaban.');
  //         }
  //       };
  //     })
  //     .catch(err => {
  //       Swal.fire("Error", "Akses mikrofon ditolak.", "error");
  //       setIsModalOpen(false);
  //     });
  // };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const playRecordedAudio = () => {
    const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    setIsPlaying(true);
    audio.play();
    audio.onended = () => setIsPlaying(false);
  };

  const closeModal = () => {
    stopRecording();
    setIsModalOpen(false);
    setIsModalSingleOpen(false);
    setRecordedAudioUrl(null);
    setBotAudioUrl(null);
  };

  const openModal = () => {
    setIsModalOpen(true);
    setRecordedAudioUrl(null);
    setBotAudioUrl(null);
  }

  const playBotAudio = () => {
    if (!botAudioUrl) return;
    const audio = new Audio(botAudioUrl);
    audio.play();
  };

  if (sessions.length === 0) {
    return (
      <div className="container-fluid d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
        <div className="text-center">
          <h4>Belum ada sesi chat</h4>
          <p>Silakan membuat sesi baru.</p>
          <ChatTabs sessions={sessions} onNewSession={handleNewSession} onSelectSession={setActiveSession} />
        </div>
      </div>
    );
  }

  const stopAudio = () => {

    Object.keys(audioRefs.current).forEach((key) => {
      audioRefs.current[key].pause();
      audioRefs.current[key].currentTime = 0;
    });
    setPlayingAudioIndex(null);
    speechSynthesis.cancel();
    setIsSpeaking(false);
    setLastMessage('');

  };

  return (
    <div className="container-fluid chat-page-layout">
      <div className="row h-100 g-3">
        <div
          className={`transition-all chat-sidebar-column ${isHovered ? 'col-md-8' : 'col-md-2'}`}
          // onMouseEnter={() => setIsHovered(true)}
          // onMouseLeave={() => setIsHovered(false)}
          style={{ transition: 'all 0.4s ease' }}
        >
          <div className="chat-sidebar-panel">
            <h6 className="mb-3">Riwayat Sesi</h6>
            <div className="chat-sidebar-scroll">
              <ChatTabs
                sessions={sessions}
                onNewSession={handleNewSession}
                onSelectSession={setActiveSession}
                activeSessionId={activeSession?.id}
                stopAudio={stopAudio}
              />
            </div>
          </div>
        </div>

        <div
          className={`${isHovered ? 'col-md-4' : 'col-md-10'} transition-all chat-main-column`}
          style={{ transition: 'all 0.4s ease' }}
        >
          <div className="chat-main-panel">
            <div className="card shadow-sm chat-main-card">
              <div className="card-body chat-main-scroll" style={{ backgroundColor: '#f8f9fa' }}>
                {messages.map((msg, i) => (
                  <div key={i} className={`mb-2 ${msg.role === 'user' ? 'text-end' : ''}`}>
                    <div className={`d-inline-block p-2 rounded ${msg.role === 'user' ? 'bg-dark text-white' : 'bg-light border'}`}>
                      {msg.type === 'text' && <Response>{msg.message}</Response>}

                      {msg.type === 'audio' && (
                        <div>
                          {/* {msg.message} */}
                          {msg.audioUrl && (
                            <button
                              className={`btn btn-sm ms-2 ${msg.role === 'user' ? 'btn-light' : 'btn-dark'}`}
                              onClick={() => {
                                const audioKey = `${msg.role}-${i}`;
                                const currentAudio = audioRefs.current[audioKey];
                                if (currentAudio) {
                                  if (!currentAudio.paused) {
                                    currentAudio.pause();
                                    currentAudio.currentTime = 0;
                                    setPlayingAudioIndex(null);
                                    speechSynthesis.cancel();
                                  } else {
                                    Object.keys(audioRefs.current).forEach((key) => {
                                      audioRefs.current[key].pause();
                                      audioRefs.current[key].currentTime = 0;
                                    });

                                    currentAudio.play();
                                    setPlayingAudioIndex(i);
                                    speechSynthesis.cancel();
                                  }
                                } else {
                                  const newAudio = new Audio(`${process.env.REACT_APP_API_BASE_URL}/${msg.audioUrl}`);

                                  Object.keys(audioRefs.current).forEach((key) => {
                                    audioRefs.current[key].pause();
                                    audioRefs.current[key].currentTime = 0;
                                  });

                                  audioRefs.current[audioKey] = newAudio;
                                  newAudio.play();
                                  setPlayingAudioIndex(i);

                                  newAudio.onended = () => {
                                    setPlayingAudioIndex(null);
                                  };
                                }
                              }}
                            >
                              <i className={`fa ${playingAudioIndex === i ? 'fa-stop' : 'fa-volume-up'}`}></i>
                              {playingAudioIndex === i ? ' Stop' : ' Play Audio'}
                            </button>
                          )}
                        </div>
                      )}

                      {
                        (msg.role != 'user' && msg.type != 'audio') && (
                          <>
                            &nbsp;
                            <button onClick={() => speak(msg.message)} className="btn btn-sm btn-dark">
                              <i className={`fa ${isSpeaking && lastMessage === msg.message ? 'fa-stop' : 'fa-volume-up'} me-2`}></i>
                              {isSpeaking && lastMessage === msg.message ? 'Stop TTS' : 'Play TTS'}
                            </button>
                          </>
                        )
                      }
                    </div>
                    {msg.exportUrl && (
                      <div className="mt-2">
                        <a href={process.env.REACT_APP_API_BASE_URL + '/chat/download/' + msg.exportUrl} className="btn btn-sm btn-outline-dark" target='_blank'>
                          <i className="fa fa-download me-1"></i> Unduh {msg.exportUrl.endsWith('.pdf') ? 'PDF' : 'DOCX'}
                        </a>
                      </div>
                    )}
                  </div>
                ))}

                {botTypingMessage && (
                  <div className="mb-2 text-start">
                    <div className="d-inline-block p-2 bg-light border rounded">
                      {botTypingMessage}
                      <span className="typing-dot">▌</span>
                    </div>
                  </div>
                )}

                {loading && (
                  <div className="text-start">
                    <div className="d-inline-block p-2 bg-light border rounded">
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Sedang berpikir...
                    </div>
                  </div>
                )}

                <div ref={bottomRef}></div>
              </div>
            </div>

            <div className="mt-3">
              <div className="d-flex">

                <div style={{ width: '100%' }}>
                  <textarea
                    className="form-control"
                    placeholder="Ketik pertanyaan Anda..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    maxLength={plan.max_text ?? 100}
                    rows={1}
                    style={{ resize: 'none', flex: 1 }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                  ></textarea>
                </div>
                <button
                  className="btn btn-dark ms-2"
                  style={{ width: '120px', height: '100%' }}
                  onClick={handleSend}
                >
                  Kirim <i className="fa fa-paper-plane"></i>
                </button>

                {plan.is_ai_audio_active == 1 && (<>

                  <button
                    className="btn btn-dark ms-2"
                    style={{ height: '100%' }}
                    onClick={startVoiceChatSingle}
                  >
                    <i className="fa fa-microphone"></i>
                  </button>

                  <button
                    className="btn btn-dark ms-2"
                    style={{ height: '100%' }}
                    onClick={startVoiceChat}
                  >
                    <i className="fa fa-volume-up"></i>
                  </button>

                </>
                )}
              </div>
              <div className="text-end text-muted small mt-1">
                {input.length}/{plan.max_text ?? 100}
              </div>
            </div>
            <center>
              <label>Model</label> &nbsp;
              <select
                value={model}
                onChange={(e) => setModel(e.target.value)}
              >
                <option value="KHUSUS">KHUSUS</option>
                <option value="UMUM">UMUM</option>
              </select>
              <br />
              <p>
                <small>* Pilih model KHUSUS untuk jawaban yang lebih terfokus pada dokumen hukum. Pilih UMUM untuk jawaban yang lebih luas dan fleksibel.</small>
              </p>
            </center>
          </div>
        </div>
      </div>

      {isModalSingle && (
        <div className="modal-backdrop" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div className="modal-content justify-content-center align-items-center bg-dark text-white" style={{
            width: '100%',
            height: '100%',
            padding: '2rem',
            borderRadius: '10px',
            position: 'relative'
          }}>
            <h2 className="mb-4 text-center">{isRecording ? 'Hentikan Rekam Suara' : 'Mulai Rekam Suara'}</h2>

            <center>
              <button
                className={`btn btn-${isRecording ? 'danger' : 'success'} rounded-circle`}
                onClick={isRecording ? stopRecording : startVoiceChatSingle}
                style={{ width: 200, height: 200 }}
                title={isRecording ? 'Stop Rekaman' : 'Mulai Rekaman'}
              >
                <i className={`fa ${isRecording ? 'fa-microphone-slash' : 'fa-microphone'} fa-5x`}></i>
              </button>
              {/* <h6 className='mt-3'>{isRecording ? 'Stop Rekaman' : 'Mulai Rekaman'}</h6> */}
            </center>

            <br />

            <div className="d-flex justify-content-center gap-3 mb-4">

              {!isRecording && (
                <button className="btn btn-secondary" onClick={closeModal}>
                  <i className="fa fa-times me-2"></i> Tutup
                </button>
              )}

              {isRecording && (
                <>
                  {/* <button className="btn btn-primary" onClick={playRecordedAudio}>
                    <i className="fa fa-play me-2"></i> Putar
                  </button> */}
                  <button className="btn btn-secondary" onClick={closeModal}>
                    <i className="fa fa-times me-2"></i> Tutup
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <div className="modal-backdrop" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div className="modal-content justify-content-center align-items-center bg-dark text-white" style={{
            width: '100%',
            height: '100%',
            padding: '2rem',
            borderRadius: '10px',
            position: 'relative'
          }}>
            <h2 className="mb-4 text-center">
              {isVoiceConnected ? 'Sesi Voice Chat Aktif' : 'Mulai Voice Chat'}
            </h2>

            <center>
              <button
                className={`btn btn-${isVoiceConnected ? 'danger' : 'success'} rounded-circle`}
                onClick={isVoiceConnected ? stopVoiceChat : startVoiceChat}
                style={{ width: 200, height: 200 }}
                title={isVoiceConnected ? 'Akhiri Voice Chat' : 'Mulai Voice Chat'}
              >
                <i className={`fa ${isVoiceConnected ? 'fa-phone-slash' : 'fa-microphone'} fa-5x`}></i>
              </button>
            </center>

            <br />

            <div className="d-flex justify-content-center gap-3 mb-4">
              {!isVoiceConnected && (
                <button className="btn btn-secondary" onClick={closeModal}>
                  <i className="fa fa-times me-2"></i> Tutup
                </button>
              )}
              {isVoiceConnected && (
                <button className="btn btn-secondary" onClick={stopVoiceChat}>
                  <i className="fa fa-stop me-2"></i> Akhiri
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatPage;
